/**
 * Viewer Performance Telemetry
 *
 * Dual-sink: performance measurements (TTFF, series/study load, scroll P95) always run and
 * are shipped to the production analytics pipeline as `viewer.perf_*` events via trackViewer
 * (batched, best-effort, PHI-scrubbed server-side). The verbose console sink stays opt-in via
 * ?telemetry=true in URL or localStorage.setItem('telemetry', 'true') — the flag itself is
 * read by the eager lib/telemetry-flag.ts, so it can be armed from any route rather than only
 * from a hand-typed /viewer URL.
 *
 * The same lifecycle also drives the Sentry study-load waterfall (lib/viewer-tracing.ts),
 * which is inert without a DSN.
 */
import { flush as flushAnalytics } from '@/lib/analytics';
import { trackViewer } from '@/lib/analytics/viewer';
import { getDeviceProfile } from '@/lib/device-profile';
import { isTelemetryConsoleEnabled } from '@/lib/telemetry-flag';
import { endSeriesLoadTrace, endStudyLoadTrace, startSeriesLoadTrace, startStudyLoadTrace, } from '@/lib/viewer-tracing';
import { getWarmInfo } from '@/lib/viewer/warmRegistry';
// ============================================
// Configuration
// ============================================
const isConsoleEnabled = () => isTelemetryConsoleEnabled();
// ============================================
// State
// ============================================
const marks = new Map();
const seriesLoadStartTimes = new Map();
// Series whose load telemetry has already been emitted in this study session. Completion
// deletes the start time, so without this a later startSeriesLoad for the same series
// (the viewer's effect re-runs on every delivered instance page) would restart tracking and
// the prefetch manager's immediate status callback would complete it a second time.
// Cleared when a different study opens (startStudyLoad).
const finishedSeries = new Set();
let lastSeriesCompletionTime = 0; // Track when last series completed for accurate throughput
// When the prefetch queue first drove each series (see markSeriesLoadDispatched) — that
// series' own load clock. Deleted on completion; pruned to the open study's series when a
// different study opens.
const seriesDispatchTimes = new Map();
const studyLoadState = {
    studyUid: null,
    startTime: 0,
    totalSeries: 0,
    completedSeries: new Set(),
    // At most one abandonment per study open — the page can hide, come back, and then unmount,
    // and all three are the same abandoned load.
    abandonEmitted: false,
};
// Track scroll events for latency measurement
let lastScrollTime = null;
let scrollLatencies = [];
let lastScrollP95EmitTime = Number.NEGATIVE_INFINITY;
// Bundle-aware prefetch (docs/planning/2026-08-25-bundle-aware-prefetch.md):
// what fraction of a study's raster images arrived via bundle transport vs
// per object, and what that transport cost/tripped. Per-series
// counts accumulate here as chunks settle and are consumed (and cleared) the
// moment that series' load completes; the byte/high-water figures are
// per-STUDY and are consumed when the whole study finishes loading, in
// checkStudyLoadComplete, next to viewer.perf_study_load.
const bundleStatsBySeries = new Map();
/** Series the per-object byte estimate below does not apply to. */
const notWanRasterSeries = new Set();
let studyBundleBytes = 0;
let studyObjectBytes = 0;
let datasetCacheHighWaterBytes = 0;
/**
 * There is no cheaper way to know actual per-object network bytes than the
 * same rough estimate `completeSeriesLoad`'s throughput calc below already
 * uses (~500KB/image) — real bytes would mean hooking Cornerstone's own
 * XHR-based wadouri loader, which is its own project. Bundle bytes need no
 * such estimate: they are counted for real, from the parsed frame lengths, at
 * the point bundleLoader seeds them.
 *
 * What the estimate DOES require is that the objects it prices are WAN-served
 * raster slices. It is a CT slice's rough size; a whole-slide pyramid's tile
 * set or an MPEG clip is nothing like it, and a study a clinic's onsite gateway
 * serves over the LAN did not cross the WAN at all. Charging those the same
 * per-object figure turned a gateway clinic's read into hundreds of megabytes
 * of imaginary egress, and the `bundle_bytes` vs `object_bytes` split — the
 * whole point of which is to show what bundling saved — read as though bundling
 * had achieved nothing. See {@link recordSeriesNotWanRaster}.
 */
const ESTIMATED_BYTES_PER_OBJECT = 500 * 1024;
// ============================================
// Analytics sink
// ============================================
/**
 * Ship one perf measurement to product analytics. `ms` is rounded to an integer; undefined
 * props are stripped. Property keys must avoid the server sanitizer's PHI denylist and no
 * study/series identifiers are sent — only the duration and categorical dimensions.
 */
function emitPerf(event, ms, props) {
    const payload = { ms: Math.round(ms) };
    if (props) {
        for (const [key, value] of Object.entries(props)) {
            if (value !== undefined)
                payload[key] = value;
        }
    }
    trackViewer(event, payload);
}
/** Same sink as {@link emitPerf}, for metrics that are not a duration. */
function emitMetric(event, props) {
    const payload = {};
    for (const [key, value] of Object.entries(props)) {
        if (value !== undefined)
            payload[key] = value;
    }
    trackViewer(event, payload);
}
/**
 * The machine's coarse hardware profile, attached to the load metrics whose numbers it
 * explains. A study that took 40s on a software rasteriser and one that took 40s on a
 * workstation GPU are different findings, and without these props they are the same row.
 * Key names are chosen to survive the server sanitizer's PHI key denylist.
 */
function deviceProps() {
    const profile = getDeviceProfile();
    return { gpu: profile.gpu, deviceMemoryGb: profile.deviceMemoryGb };
}
// ============================================
// Core Functions
// ============================================
/**
 * Record a timestamp mark
 */
function mark(name, metadata) {
    marks.set(name, {
        name,
        timestamp: performance.now(),
        metadata,
    });
}
/**
 * Measure duration between a start mark and now, log to console
 */
function measure(name, startMarkName, metadata) {
    const startMark = marks.get(startMarkName);
    if (!startMark) {
        if (isConsoleEnabled()) {
            console.warn(`[Telemetry] Start mark "${startMarkName}" not found`);
        }
        return null;
    }
    const duration = performance.now() - startMark.timestamp;
    const combinedMetadata = { ...startMark.metadata, ...metadata };
    logMetric({
        name,
        value: duration,
        unit: 'ms',
        metadata: combinedMetadata,
    });
    // Clean up the start mark
    marks.delete(startMarkName);
    return duration;
}
/**
 * Format duration in human-readable form
 */
function formatDuration(ms) {
    if (ms < 1000) {
        return `${ms.toFixed(1)}ms`;
    }
    else if (ms < 60000) {
        return `${(ms / 1000).toFixed(1)}s`;
    }
    else {
        const minutes = Math.floor(ms / 60000);
        const seconds = ((ms % 60000) / 1000).toFixed(0);
        return `${minutes}m ${seconds}s`;
    }
}
/**
 * Log a metric to console with formatting
 */
function logMetric(metric) {
    if (!isConsoleEnabled())
        return;
    const metadataStr = metric.metadata
        ? ` | ${Object.entries(metric.metadata)
            .map(([k, v]) => `${k}: ${v}`)
            .join(', ')}`
        : '';
    let valueFormatted;
    if (metric.unit === 'ms') {
        valueFormatted = formatDuration(metric.value);
    }
    else if (metric.unit === 'MB/s') {
        valueFormatted = `${metric.value.toFixed(2)} MB/s`;
    }
    else {
        valueFormatted = `${metric.value} ${metric.unit}`;
    }
    console.log(`%c[Telemetry] %c${metric.name}%c ${valueFormatted}%c${metadataStr}`, 'color: #888', 'color: #4CAF50; font-weight: bold', 'color: #2196F3; font-weight: bold', 'color: #888');
}
// ============================================
// TTFF (Time to First Frame)
// ============================================
/**
 * Mark the start of loading a series (call when series is selected). A
 * `trigger` in the metadata rides through to `viewer.perf_ttff`.
 */
export function markSeriesStart(seriesUid, metadata) {
    mark(`ttff:${seriesUid}`, { seriesUid, ...metadata });
}
/**
 * Mark the first frame rendered (call on first IMAGE_RENDERED)
 */
export function measureTTFF(seriesUid, metadata) {
    const startMetadata = marks.get(`ttff:${seriesUid}`)?.metadata;
    const duration = measure('TTFF (Time to First Frame)', `ttff:${seriesUid}`, {
        seriesUid,
        ...metadata,
    });
    if (duration !== null) {
        const combined = { ...startMetadata, ...metadata };
        // Whether the worklist warmed this series' first image before the viewer
        // asked for it, and how much lead it had. Keyed by the same series id.
        const warm = getWarmInfo(seriesUid);
        emitPerf('viewer.perf_ttff', duration, {
            modality: combined.modality,
            imageCount: combined.imageCount,
            // 'select' when the reader put this series into a pane (a thumbnail
            // click and the like) and the clock ran from that moment; absent for the
            // panes that open with the study, as every TTFF before this tag was.
            trigger: combined.trigger,
            warmed: warm?.warmed,
            warm_lead_ms: warm?.warmLeadMs,
            ...deviceProps(),
        });
    }
    return duration;
}
// ============================================
// Series Load Time
// ============================================
/**
 * Start tracking series load time
 */
export function startSeriesLoad(seriesUid, imageCount) {
    // Don't restart tracking if already tracking this series, or if it already finished
    if (seriesLoadStartTimes.has(seriesUid) || finishedSeries.has(seriesUid))
        return;
    seriesLoadStartTimes.set(seriesUid, performance.now());
    startSeriesLoadTrace(seriesUid);
    if (isConsoleEnabled()) {
        console.log(`%c[Telemetry] %cSeries load started%c ${seriesUid.slice(-8)}... (${imageCount} images)`, 'color: #888', 'color: #FF9800', 'color: #888');
    }
}
/**
 * The prefetch queue has dispatched this series' first loads (not merely reached it: a
 * series waiting for a slot, or held at the look-ahead ceiling, has not started loading).
 * Its first call per series starts
 * that series' own load clock, which `completeSeriesLoad` measures to — later calls (a
 * re-drive after a deprioritize, a page landing) leave it alone.
 */
export function markSeriesLoadDispatched(seriesUid) {
    if (finishedSeries.has(seriesUid) || seriesDispatchTimes.has(seriesUid))
        return;
    seriesDispatchTimes.set(seriesUid, performance.now());
}
/**
 * Mark series load complete
 */
export function completeSeriesLoad(seriesUid, imageCount) {
    const startTime = seriesLoadStartTimes.get(seriesUid);
    if (startTime === undefined)
        return;
    const now = performance.now();
    // The viewer registers every series of a study at once, so a series' own start mark is
    // effectively "when the viewer opened" — this is the cumulative figure, not this series'.
    const sinceOpenMs = now - startTime;
    // This series' own load time: from when the prefetch queue first drove it to now. Small
    // series without bundle coverage load OVERLAPPED (an X-ray study's one-image series run
    // side by side — see handsOffEarly in prefetchManager), so the gap since the previous
    // series finished no longer measures any one series; for series that run one at a time
    // (CT/MR) the two readings agree, the queue starting each as the last one finishes.
    //
    // A series the queue never drove (its images all loaded by the viewport itself) falls
    // back to that gap, seeded to the study's start (see startStudyLoad) so the first series
    // of a study measures from the study open rather than from whatever the last study in
    // this session happened to finish.
    const dispatchedAt = seriesDispatchTimes.get(seriesUid);
    seriesDispatchTimes.delete(seriesUid);
    const seriesLoadMs = dispatchedAt !== undefined
        ? now - dispatchedAt
        : lastSeriesCompletionTime > 0
            ? now - lastSeriesCompletionTime
            : sinceOpenMs;
    lastSeriesCompletionTime = now;
    seriesLoadStartTimes.delete(seriesUid);
    finishedSeries.add(seriesUid);
    endSeriesLoadTrace(seriesUid);
    // Calculate throughput based on this series' own load time (rough estimate: ~500KB per image for CT)
    const estimatedMB = (imageCount * 500) / 1024;
    const throughputMBps = estimatedMB / (seriesLoadMs / 1000);
    logMetric({
        name: 'Series Load Time',
        value: sinceOpenMs,
        unit: 'ms',
        metadata: {
            seriesUid: seriesUid.slice(-12),
            imageCount,
            actualTime: formatDuration(seriesLoadMs),
            throughput: `${throughputMBps.toFixed(1)} MB/s`,
        },
    });
    // `ms` is THIS SERIES' own load duration; `sinceOpenMs` is viewer-open → this series
    // complete, i.e. the cumulative figure. (Before series could overlap, `ms` was the gap
    // since the previous series completed — the same quantity for series that load one at a
    // time, and a shorter, meaningless one for overlapped series.) Recorded history before this distinction existed
    // carries the cumulative number in `ms`, so a percentile series that spans the change is
    // comparing two different quantities — compare `sinceOpenMs` against the old `ms`, or
    // start the per-series series fresh.
    emitPerf('viewer.perf_series_load', seriesLoadMs, {
        imageCount,
        sinceOpenMs: Math.round(sinceOpenMs),
    });
    // bundle_utilization — the PR's acceptance metric: what fraction of this
    // series' objects arrived via bundle transport rather than per object. A
    // series bundling never touched (LAN study, disabled, nothing retained) has
    // no entry here, and that is a real 0%, not a gap — default rather than
    // skip, so the denominator for "80% of series" is every series, not just
    // the ones that happened to bundle.
    const bundleStats = bundleStatsBySeries.get(seriesUid) ?? { seededCount: 0, failedCount: 0 };
    bundleStatsBySeries.delete(seriesUid);
    const bundledObjects = Math.min(imageCount, bundleStats.seededCount);
    const utilizationPct = imageCount > 0 ? Math.round((bundledObjects / imageCount) * 100) : 0;
    emitMetric('viewer.perf_bundle_utilization', {
        utilizationPct,
        seededCount: bundleStats.seededCount,
        failedCount: bundleStats.failedCount,
        imageCount,
    });
    // bundle_bytes vs object_bytes half of the pairing: the objects THIS series
    // did not get from a bundle went per object, at the estimate above. The
    // bundle half is real bytes, added by bundleLoader as chunks seed.
    //
    // Series the estimate does not describe are left out entirely rather than
    // approximated with a different constant — an all-LAN study reports ~0 WAN
    // object bytes, which is the truth, and a made-up figure for a clip would
    // only be a more confident kind of wrong.
    if (!notWanRasterSeries.has(seriesUid)) {
        studyObjectBytes += Math.max(0, imageCount - bundledObjects) * ESTIMATED_BYTES_PER_OBJECT;
    }
    // Update study load tracking
    if (studyLoadState.studyUid) {
        studyLoadState.completedSeries.add(seriesUid);
        checkStudyLoadComplete();
    }
}
/**
 * Start tracking study load time
 */
export function startStudyLoad(studyUid, seriesUids, meta) {
    // Don't restart tracking if already tracking this study
    if (studyLoadState.studyUid === studyUid)
        return;
    // Opening a different study while the previous one is still loading is an abandonment of
    // that previous load, and the only chance to record it — its state is about to be reset.
    // Queued, not flushed: the user is still here, so the normal batch timer will carry it.
    emitStudyLoadAbandoned();
    studyLoadState.studyUid = studyUid;
    studyLoadState.startTime = performance.now();
    studyLoadState.totalSeries = seriesUids.length;
    studyLoadState.completedSeries.clear();
    finishedSeries.clear();
    studyLoadState.abandonEmitted = false;
    // Seeds the per-series clock (see completeSeriesLoad). Without this the first series of
    // the second study opened in a session is charged everything since the FIRST study's last
    // series finished — reading room idle time included.
    lastSeriesCompletionTime = studyLoadState.startTime;
    // Dispatch clocks belong to the study they were started in. Kept for THIS study's series:
    // the viewer registers and prioritizes them in the same commit that calls this, and the
    // prefetch queue can start driving them first.
    const openedSeries = new Set(seriesUids);
    for (const seriesUid of seriesDispatchTimes.keys()) {
        if (!openedSeries.has(seriesUid))
            seriesDispatchTimes.delete(seriesUid);
    }
    // A study abandoned before it ever completed (viewer closed mid-load) would
    // otherwise leak its partial byte/peak tallies into the next study opened
    // in the same session.
    bundleStatsBySeries.clear();
    notWanRasterSeries.clear();
    studyBundleBytes = 0;
    studyObjectBytes = 0;
    datasetCacheHighWaterBytes = 0;
    startStudyLoadTrace({
        seriesCount: seriesUids.length,
        imageCount: meta?.imageCount,
        modality: meta?.modality,
    });
    if (isConsoleEnabled()) {
        console.log(`%c[Telemetry] %cStudy load started%c ${studyUid.slice(-8)}... (${seriesUids.length} series)`, 'color: #888', 'color: #FF9800', 'color: #888');
    }
}
/**
 * Check if all series are loaded and log study load time
 */
function checkStudyLoadComplete() {
    if (!studyLoadState.studyUid)
        return;
    if (studyLoadState.completedSeries.size < studyLoadState.totalSeries)
        return;
    const duration = performance.now() - studyLoadState.startTime;
    logMetric({
        name: 'Study Load Time (all series cached)',
        value: duration,
        unit: 'ms',
        metadata: {
            studyUid: studyLoadState.studyUid.slice(-12),
            seriesCount: studyLoadState.totalSeries,
        },
    });
    emitPerf('viewer.perf_study_load', duration, {
        seriesCount: studyLoadState.totalSeries,
        ...deviceProps(),
    });
    // bundle_bytes vs object_bytes — the byte split for the whole study open —
    // and the dataset cache's observed peak, alongside whether the prefetcher's
    // dataset-cache high-water gate ever tripped (see
    // recordDatasetCacheGateTripped, fired at the trip itself since that is a
    // discrete event worth seeing as it happens).
    emitMetric('viewer.perf_bundle_bytes', {
        bundleBytes: studyBundleBytes,
        objectBytes: Math.round(studyObjectBytes),
    });
    if (datasetCacheHighWaterBytes > 0) {
        emitMetric('viewer.perf_dataset_cache_high_water', {
            mb: Math.round((datasetCacheHighWaterBytes / (1024 * 1024)) * 10) / 10,
        });
    }
    studyBundleBytes = 0;
    studyObjectBytes = 0;
    datasetCacheHighWaterBytes = 0;
    endStudyLoadTrace('complete');
    // Reset state
    studyLoadState.studyUid = null;
}
/**
 * Queue `viewer.perf_study_load_abandoned` if a study load is in flight and hasn't already
 * been reported abandoned. Returns whether anything was emitted.
 *
 * The study state is deliberately left in place: a reader who tabs away for ten seconds and
 * comes back is recorded as having abandoned the load (they did stop watching it) but the
 * load itself carries on, and `viewer.perf_study_load` still fires if it finishes. Nulling
 * the state here instead would have traded one blind spot for another.
 */
function emitStudyLoadAbandoned() {
    if (!studyLoadState.studyUid)
        return false;
    if (studyLoadState.abandonEmitted)
        return false;
    studyLoadState.abandonEmitted = true;
    const duration = performance.now() - studyLoadState.startTime;
    logMetric({
        name: 'Study Load ABANDONED',
        value: duration,
        unit: 'ms',
        metadata: {
            studyUid: studyLoadState.studyUid.slice(-12),
            completed: studyLoadState.completedSeries.size,
            total: studyLoadState.totalSeries,
        },
    });
    emitPerf('viewer.perf_study_load_abandoned', duration, {
        completedSeries: studyLoadState.completedSeries.size,
        totalSeries: studyLoadState.totalSeries,
        ...deviceProps(),
    });
    endStudyLoadTrace('abandoned');
    return true;
}
/**
 * The reader gave up: the viewer is unmounting, or the page is going away, while a study
 * load is still incomplete.
 *
 * `viewer.perf_study_load` only fires when EVERY series completes, so until this existed the
 * worst experiences in the product — the loads people walked away from — emitted nothing at
 * all, and the study-load percentiles were computed over survivors only. Fires at most once
 * per study open (hide-then-unmount is one abandonment, not two) and never after a study has
 * already completed.
 *
 * Flushes with keepalive rather than waiting for the 5s batch timer, which a page being
 * unloaded will not live to see. The flush is the SDK's normal authenticated POST (the
 * access token is still in memory while the handler runs, and `fetch(…, {keepalive: true})`
 * carries the Authorization header — `navigator.sendBeacon` cannot, and the collect endpoint
 * requires auth).
 */
export function abandonStudyLoad() {
    if (!emitStudyLoadAbandoned())
        return;
    flushAnalytics(true);
}
// ============================================
// Scroll Latency
// ============================================
/** Minimum gap between analytics emissions of the scroll P95 — the per-session P95 is stable,
 *  and heavy scrolling would otherwise fill a 20-sample batch every couple of seconds. */
const SCROLL_P95_EMIT_MIN_INTERVAL_MS = 30_000;
/**
 * Mark a scroll event (call on wheel event)
 */
export function markScroll() {
    lastScrollTime = performance.now();
}
/**
 * Measure scroll latency (call on IMAGE_RENDERED after scroll)
 */
export function measureScrollLatency() {
    if (lastScrollTime === null)
        return;
    const latency = performance.now() - lastScrollTime;
    lastScrollTime = null;
    // Only track reasonable latencies (filter out unrelated renders)
    if (latency < 1000) {
        scrollLatencies.push(latency);
        // Log individual latency
        logMetric({
            name: 'Scroll Latency',
            value: latency,
            unit: 'ms',
        });
        // Log P95 every 20 samples
        if (scrollLatencies.length >= 20) {
            const sorted = scrollLatencies.toSorted((a, b) => a - b);
            const p95Index = Math.floor(sorted.length * 0.95);
            const p95 = sorted[p95Index];
            const samples = scrollLatencies.length;
            logMetric({
                name: 'Scroll Latency P95',
                value: p95,
                unit: 'ms',
                metadata: { samples },
            });
            const now = performance.now();
            if (now - lastScrollP95EmitTime >= SCROLL_P95_EMIT_MIN_INTERVAL_MS) {
                lastScrollP95EmitTime = now;
                emitPerf('viewer.perf_scroll_p95', p95, { samples });
            }
            // Reset for next batch
            scrollLatencies = [];
        }
    }
}
// ============================================
// Bundle Transport
// ============================================
//
// docs/planning/2026-08-25-bundle-aware-prefetch.md, §Telemetry. Five signals
// so product's next cold-load measurement can't miss what the last one caught
// only by counting network requests by hand.
/**
 * Record one resolved bundle chunk's contribution to a series' object mix.
 *
 * Called from `bundleLoader` where a chunk SETTLES, which is the one point both
 * transports pass through — a page bundled at delivery and a chunk the
 * prefetcher fetched alike. Recording at either call site instead counted that
 * caller's chunks only, and a focused series bundled end to end at delivery
 * therefore read 0% utilization.
 *
 * Every settled outcome counts except a cancel: a `failed` chunk's
 * `seededCount` is whatever partial credit it delivered before giving up, which
 * is exactly what "arrived via bundle" means, while a `skipped` one is not a
 * verdict at all and the pass that re-fetches it will report what it finds.
 * Accumulated per series and consumed (and cleared) the moment that series'
 * load completes, in {@link completeSeriesLoad}.
 */
export function recordBundleChunkResult(seriesUID, result) {
    const stats = bundleStatsBySeries.get(seriesUID) ?? { seededCount: 0, failedCount: 0 };
    stats.seededCount += result.seededCount;
    stats.failedCount += result.failedCount;
    bundleStatsBySeries.set(seriesUID, stats);
}
/**
 * This series' objects are not WAN-served raster slices, so its unbundled
 * objects must not be priced as though they were: it is a video or whole-slide
 * series, whose objects the flat per-image estimate misprices by an order of
 * magnitude, or its clinic serves the study off the LAN, in which case those
 * bytes never crossed the WAN at all.
 *
 * Recorded by `shouldBundlePage`, which is the single place that verdict is
 * already reached — once per series, on every delivery path, whether or not
 * bundling itself is switched on. Idempotent, and forgotten with the study.
 */
export function recordSeriesNotWanRaster(seriesUID) {
    notWanRasterSeries.add(seriesUID);
}
/**
 * Real bytes streamed via bundle transport, from a chunk's parsed frame
 * lengths — accumulated for the whole study and shipped once it finishes
 * loading (`viewer.perf_bundle_bytes`, alongside `viewer.perf_study_load`).
 */
export function recordBundleBytes(bytes) {
    if (bytes > 0)
        studyBundleBytes += bytes;
}
/**
 * Sample the dataset cache's byte size, wherever the prefetcher already reads
 * it for the high-water gate. Tracked as a running peak for the study and
 * shipped as `dataset_cache_high_water_mb` once it finishes loading.
 */
export function recordDatasetCacheBytes(bytes) {
    if (bytes > datasetCacheHighWaterBytes)
        datasetCacheHighWaterBytes = bytes;
}
/**
 * The one-way dataset-cache high-water latch in prefetchManager just tripped:
 * bundle transport is off for the rest of this study. The latch only flips
 * once per study, so this fires at most once and needs no throttling of its
 * own.
 */
export function recordDatasetCacheGateTripped() {
    emitMetric('viewer.perf_dataset_cache_gate_tripped', {});
}
/**
 * One bundle chunk failed to fully seed. `broke` is true only when this
 * actually tripped the series' breaker (permanent status, transport, stall,
 * framing) — the other three reasons are the chunk giving up on its own; the
 * series keeps bundling its next chunk.
 */
export function recordBundleBreakerReason(reason, broke) {
    emitMetric('viewer.perf_bundle_breaker_trip', { reason, broke });
}
/**
 * One 429 admission shed, whether or not the chunk goes on to recover on
 * retry — the numerator for a shed rate product can watch as the global slot
 * cap gets tuned.
 */
export function recordBundleShed() {
    emitMetric('viewer.perf_bundle_shed', {});
}
/** A retained manifest aged past its usable life was skipped, not fetched. */
export function recordManifestExpiredSkip() {
    emitMetric('viewer.perf_manifest_expired_skip', {});
}
// ============================================
// Generic events
// ============================================
/**
 * Emit a named telemetry event. Use this for discrete user/product signals
 * (e.g. feature overrides, adoption pings) rather than performance timing.
 * Shape is `{ name, payload, timestamp }` so downstream ingestion can stay
 * agnostic to the console sink we use today.
 */
export function trackEvent(name, payload) {
    if (!isConsoleEnabled())
        return;
    const timestamp = new Date().toISOString();
    const payloadStr = payload
        ? ` | ${Object.entries(payload)
            .map(([k, v]) => `${k}: ${typeof v === 'string' ? v : JSON.stringify(v)}`)
            .join(', ')}`
        : '';
    console.log(`%c[Telemetry] %cevent%c ${name}%c ${timestamp}${payloadStr}`, 'color: #888', 'color: #9C27B0; font-weight: bold', 'color: #2196F3; font-weight: bold', 'color: #888');
}
// ============================================
// Utility
// ============================================
/**
 * Log current telemetry status
 */
export function logStatus() {
    if (!isConsoleEnabled()) {
        console.log('[Telemetry] Console sink disabled (perf metrics still ship to analytics). Enable with ?telemetry=true or localStorage.setItem("telemetry", "true")');
        return;
    }
    console.log('%c[Telemetry] Enabled', 'color: #4CAF50; font-weight: bold');
    console.log('  Active marks:', marks.size);
    console.log('  Series loading:', seriesLoadStartTimes.size);
    console.log('  Study tracking:', studyLoadState.studyUid ? 'active' : 'inactive');
}
// ============================================
// Worklist warm-up
// ============================================
/**
 * One `viewer.perf_warmup` per warm-up that settles (lib/cornerstone/studyWarmup):
 * what it targeted, what it actually loaded, and whether the reader cancelled
 * it by closing the card. Read next to `viewer.perf_ttff`'s `warmed` tag: the
 * pair says how often a warm-up runs to completion AND whether that moved
 * first paint.
 */
export function recordStudyWarmup(result) {
    emitMetric('viewer.perf_warmup', {
        study_id: result.studyId,
        trigger: result.trigger,
        targets: result.targets,
        loaded: result.loaded,
        already_cached: result.alreadyCached,
        depth_targets: result.depthTargets,
        depth_loaded: result.depthLoaded,
        cancelled: result.cancelled,
        skipped: result.skipped ?? undefined,
        ms: result.ms,
    });
}
